import { Settings } from 'lucide-react';
import { Toggle } from './Toggle';
import { FileDropZone } from './FileDropZone';
import { Stats } from './Stats';

/**
 * Options Panel Component
 */
export function OptionsPanel({ 
  options, 
  onToggle, 
  onSetNum, 
  onSetText,
  onDrop,
  onDragOver,
  onOpenFile,
  error,
  stats,
  hasData
}) {
  return (
    <div className="card p-6 space-y-6 animate-fade-in">
      {!hasData && (
        <div className="pt-4 border-t border-slate-200">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Load JSON</h3>
          <FileDropZone 
            onDrop={onDrop}
            onDragOver={onDragOver}
            onClick={onOpenFile}
          />
          {error && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
        </div>
      )}
      <div className="flex items-center gap-2 border-b border-slate-200 pb-4">
        <Settings className="w-5 h-5 text-slate-700" />
        <h2 className="text-lg font-semibold text-slate-900">Conversion Options</h2>
      </div>

      <div className="space-y-4">
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Content</h3>
          <Toggle 
            checked={options.includeFrontmatter} 
            onChange={() => onToggle("includeFrontmatter")} 
            label="YAML Frontmatter" 
            hint="Add metadata header with title and date" 
          />
          <Toggle 
            checked={options.includeRunSettings} 
            onChange={() => onToggle("includeRunSettings")} 
            label="Run Settings" 
            hint="Model, temperature, and other parameters" 
          />
          <Toggle 
            checked={options.includeSystemInstruction} 
            onChange={() => onToggle("includeSystemInstruction")} 
            label="System Instruction"
            hint="Include system prompt if present"
          />
          <Toggle 
            checked={options.roleHeadings} 
            onChange={() => onToggle("roleHeadings")} 
            label="Role Headings" 
            hint="Add headings for User/Assistant messages" 
          />
        </div>

        <div className="space-y-3 pt-4 border-t border-slate-200">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Advanced</h3>
          <Toggle 
            checked={options.includeHiddenThoughts} 
            onChange={() => onToggle("includeHiddenThoughts")} 
            label="Hidden Thoughts" 
            hint="Include content marked as thoughts" 
          />
          <Toggle 
            checked={options.includeGrounding} 
            onChange={() => onToggle("includeGrounding")} 
            label="Grounding References" 
            hint="Add citations and URLs section" 
          />
          <Toggle 
            checked={options.collapseWhitespace} 
            onChange={() => onToggle("collapseWhitespace")} 
            label="Collapse Whitespace" 
            hint="Normalize and trim spacing" 
          />
        </div>

        <div className="space-y-3 pt-4 border-t border-slate-200">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Formatting</h3>
          
          <div className="flex items-center justify-between gap-3">
            <label className="text-sm font-medium text-slate-700">Hard Wrap Width</label>
            <input 
              type="number" 
              min={0} 
              value={options.wrapWidth} 
              onChange={(e) => onSetNum("wrapWidth", e.target.value)} 
              className="input w-24 text-sm"
              placeholder="0 = off"
            />
          </div>
          
          <div className="flex items-center justify-between gap-3">
            <label className="text-sm font-medium text-slate-700">Heading Style</label>
            <select 
              value={options.headingStyle} 
              onChange={(e) => onSetText("headingStyle", e.target.value)} 
              className="input w-36 text-sm"
            >
              <option value="atx">ATX (#)</option>
              <option value="setext">Setext (underlines)</option>
            </select>
          </div>
        </div>
      </div>

      {hasData && stats && <Stats stats={stats} />}
    </div>
  );
}

